import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Badge from "../../../components/common/Badge";
import ActionButton from "../../../components/common/ActionButton";
import CashingUpReportPage from "./CashingUpReportPage";
import { useGetCashingUpSessionsReportQuery } from "../../../store/api";
import { formatDateTime } from "../../../utils/reporting";
import {
  CASHING_UP_STATUS_OPTIONS,
  formatAmountWithImpact,
  formatSignedCurrency,
  formatStatusLabel,
  getStatusTone,
} from "../../../utils/cashingUpReporting";

const columns = [
  { key: "businessDate", label: "BUSINESS DATE" },
  { key: "tillName", label: "TILL" },
  { key: "status", label: "STATUS" },
  { key: "expectedCash", label: "EXPECTED CASH" },
  { key: "actualCash", label: "ACTUAL CASH" },
  { key: "cashVariance", label: "VARIANCE" },
  { key: "syncStatus", label: "SYNC" },
  { key: "openedAt", label: "OPENED AT" },
  { key: "actions", label: "ACTIONS" },
];

const CashSessionsReport = () => {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <CashingUpReportPage
      title="Cash Sessions Report"
      subtitle="Central mirror of till session lifecycle, cash totals, and approval state."
      useQueryHook={useGetCashingUpSessionsReportQuery}
      collectionKeys={["sessions", "items", "rows", "data"]}
      columns={columns}
      showBusinessDate={false}
      showDateRange
      showStatuses
      showLimit={false}
      exportFileName="cash-sessions.csv"
      statusOptions={CASHING_UP_STATUS_OPTIONS}
      buildRows={(collection) =>
        collection.map((session) => ({
          id: session.cashSessionId,
          cashSessionId: session.cashSessionId || "-",
          storeId: session.storeId || "",
          storeName: session.storeName || session.storeId || "-",
          businessDate: session.businessDate || "-",
          tillName: session.tillName || session.tillId || "-",
          statusValue: session.status || "",
          status: (
            <Badge tone={getStatusTone(session.status)}>
              {formatStatusLabel(session.status)}
            </Badge>
          ),
          expectedCashValue: Number(session.expectedCash || 0),
          expectedCash: formatSignedCurrency(session.expectedCash),
          actualCashValue: Number(session.actualCash || 0),
          actualCash: formatSignedCurrency(session.actualCash),
          cashVarianceValue: Number(session.cashVariance || 0),
          cashVariance: (() => {
            const delta = Number(session.cashVariance || 0);
            const formatting = formatAmountWithImpact(delta);
            return (
              <span
                className={
                  formatting.tone === "rose"
                    ? "font-semibold text-rose-700"
                    : formatting.tone === "emerald"
                      ? "font-semibold text-emerald-700"
                      : "font-semibold text-slate-700"
                }
              >
                {formatting.value}
              </span>
            );
          })(),
          approvalStatusValue: session.approvalStatus || "",
          approvalStatus: (
            <Badge tone={getStatusTone(session.approvalStatus)}>
              {formatStatusLabel(session.approvalStatus)}
            </Badge>
          ),
          syncStatusValue: session.syncStatus || "",
          syncStatus: (
            <Badge tone={getStatusTone(session.syncStatus)}>
              {formatStatusLabel(session.syncStatus)}
            </Badge>
          ),
          openedAt: formatDateTime(session.openedAt),
          openedBy: session.openedBy || session.openedByUserId || "-",
          actions: (
            <ActionButton
              label="View session"
              icon="view"
              onClick={() =>
                navigate(
                  {
                    pathname: `/reporting/cashing-up/sessions/${session.cashSessionId}`,
                    search: location.search,
                  },
                  {
                    state: { session },
                  },
                )
              }
            />
          ),
        }))
      }
      buildStats={(_, rows) => {
        const sessionCount = rows.length;
        const closedCount = rows.filter((row) =>
          String(row.statusValue || "").toUpperCase().includes("CLOSED"),
        ).length;
        const totalVariance = rows.reduce(
          (sum, row) => sum + Number(row.cashVarianceValue || 0),
          0,
        );

        return [
          {
            label: "Sessions",
            value: String(sessionCount),
            helper: "Rows returned by the Central report.",
            accent: "indigo",
          },
          {
            label: "Closed",
            value: String(closedCount),
            helper: "Sessions already closed in Central.",
            accent: "emerald",
          },
          {
            label: "Net Variance",
            value: formatSignedCurrency(totalVariance),
            helper: "Summed variance across visible rows.",
            accent: "rose",
          },
        ];
      }}
      emptyText="No cash sessions found for the selected store and filters."
    />
  );
};

export default CashSessionsReport;
